/**
 * The browser's own ninety-minute clock, so a window that has been left alone signs itself
 * out instead of waiting to be refused.
 *
 * The Worker has enforced the idle window since it was asked for, and correctly: ninety
 * minutes without a request from an account and the next one is refused with
 * `code: "idle_timeout"`, whereupon api.js sends the browser through Access's logout URL.
 * What that leaves out is the window nobody is touching. A tab left open over lunch keeps
 * a fully drawn archive on screen — every record it had loaded still legible — and only
 * discovers the session has gone when something asks it to. Which is on the way back:
 * click into the window, the app makes a request, the request is refused, and the screen
 * that has been sitting there all along is replaced by the login page with an error
 * flashing past on the way. Both halves of that are wrong. The window should not have
 * been readable, and coming back to it should not look like a fault.
 *
 * So the clock is kept here as well, on the same ninety minutes, and the browser leaves
 * on its own rather than being told to. Nothing is refused, so there is no error to
 * render, and the archive is not sitting on a screen ninety minutes after anybody last
 * looked at it.
 *
 * **Two clocks, and this is the stricter one.** The Worker measures per *account*, from
 * the last request it saw, and writes `last_seen_at` at most every five minutes — so its
 * window is really ninety to ninety-five minutes, and a phone on the same login keeps a
 * desktop's session alive. This measures per *window*, from the last thing anybody did to
 * it, which is what the complaint was about: the window is what stays on screen. Being
 * the stricter of the two is also what makes it useful — it fires first, so the doomed
 * request never happens.
 *
 * **What counts as activity** is a pointer, a key, a wheel or a touch, plus any request
 * that came back successfully — the last because it is what the Worker's own column
 * records, and a page busy loading itself has plainly not been abandoned. Focus does not
 * count. Focus is the event this exists to catch, and a clock that reset on it would
 * never fire on the one window that needed it.
 */
import { useEffect } from 'react'

/**
 * Kept the same as IDLE_MINUTES in worker/auth.js and worker/donorAuth.js, and there is
 * no way to import it — the Worker's copy would drag the whole module into the bundle.
 * Change one and change all three; the two doors and the browser should not disagree
 * about how long ninety minutes is.
 */
export const IDLE_MS = 90 * 60_000

/**
 * What to do with a window that has gone quiet. Pure, so the decision can be tested
 * without a browser to leave — the same reason shouldRefuseAsIdle and
 * reauthenticationPlan are.
 *
 * A hidden window is told to `hold` rather than to leave: it has nothing on screen to be
 * read over anybody's shoulder, and leaving would spend the org-wide Access logout on
 * behalf of somebody who may be working in another tab behind the same team domain. It
 * has not got away with anything — becoming visible runs this again, and by then the
 * answer is `leave`, which is why the way back to a stale window is the login page and
 * not the archive.
 */
export function idleVerdict({ idleFor, visible }) {
  if (idleFor < IDLE_MS) return 'wait'
  return visible ? 'leave' : 'hold'
}

/** How long to wait before asking again. At least a second, so a bad clock cannot spin. */
export const nextCheckIn = (idleFor) => Math.max(IDLE_MS - idleFor, 1_000)

let lastActivity = Date.now()

/**
 * Called by api.js on every successful response, and by the listeners below. Deliberately
 * nothing more than a timestamp: it runs on keystrokes, and the timer reads it when it
 * wakes rather than being rescheduled by it.
 */
export const markActivity = () => {
  lastActivity = Date.now()
}

export const idleFor = () => Date.now() - lastActivity

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart']

/**
 * Watch this window, and call `onIdle` once when it has been left alone for the window
 * above — immediately if it is on screen, and otherwise the moment it comes back.
 *
 * `enabled` is false wherever there is no session to end: locally, where `wrangler dev`
 * runs the `DEV_EMAIL` branch with no Access in front of it and the logout URL would be a
 * 404. See canSignOut.
 */
export function useIdleTimeout({ enabled = true, onIdle }) {
  useEffect(() => {
    if (!enabled) return

    let timer = null
    let gone = false

    const check = () => {
      if (gone) return
      clearTimeout(timer)
      timer = null
      const quiet = idleFor()
      const verdict = idleVerdict({ idleFor: quiet, visible: !document.hidden })
      if (verdict === 'leave') {
        gone = true
        onIdle()
        return
      }
      // `hold` waits for the visibility change below rather than for the clock: the
      // window is already past the window and no amount of waiting alters that.
      if (verdict === 'wait') timer = setTimeout(check, nextCheckIn(quiet))
    }

    const touch = () => markActivity()

    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, touch, { passive: true, capture: true })
    }
    document.addEventListener('visibilitychange', check)
    check()

    return () => {
      clearTimeout(timer)
      for (const type of ACTIVITY_EVENTS) {
        window.removeEventListener(type, touch, { capture: true })
      }
      document.removeEventListener('visibilitychange', check)
    }
  }, [enabled, onIdle])
}
